/**
 * User Detail Page
 * 
 * Displays detailed information about a specific user
 * - Fetches user data by reference (UUID)
 * - Shows profile information
 * - Displays account details
 * - Shows status and metadata
 */

import { FC } from 'react';
import { useParams } from 'react-router-dom';
import { 
  FiArrowLeft, 
  FiUser, 
  FiMail, 
  FiCalendar,
  FiEdit2,
  FiRefreshCw,
  FiAlertCircle,
  FiLoader,
  FiCheckCircle,
  FiMinusCircle,
  FiXCircle,
  FiHome,
  FiHash,
} from 'react-icons/fi';
import { useDetailPage } from '@/hooks';
import { userService } from '@/services';
import Button from '@/components/Common/Button';
import Modal from '@/components/Common/Modal';
import UserForm from '@/components/Forms/UserForm';
import type { UserFormData } from '@/components/Forms/UserForm';
import styles from './UserDetailPage.module.css';

/**
 * Format date to readable string
 */
const formatDate = (dateString?: string): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

/**
 * Get status label in Portuguese
 */
const getStatusLabel = (status: string): string => {
  const statusLabels: Record<string, string> = {
    active: 'Ativo',
    inactive: 'Inativo',
    blocked: 'Bloqueado',
  };
  return statusLabels[status] || status;
};

/**
 * Get status icon
 */
const getStatusIcon = (status: string) => {
  const iconProps = { size: 16 };
  const statusIcons: Record<string, JSX.Element> = {
    active: <FiCheckCircle {...iconProps} />,
    inactive: <FiMinusCircle {...iconProps} />,
    blocked: <FiXCircle {...iconProps} />,
  };
  return statusIcons[status] || null;
};

const UserDetailPage: FC = () => {
  const { reference } = useParams<{ reference: string }>();

  /**
   * Use detail page hook
   */
  const {
    data: user,
    isLoading,
    isError,
    error,
    refetch,
    isEditModalOpen,
    setIsEditModalOpen,
    handleBack,
    updateMutation,
  } = useDetailPage({
    reference,
    service: {
      getByReference: userService.getUserByReference,
      update: userService.updateUser,
    },
    queryKey: 'user',
    navigationPath: '/users',
    successMessages: {
      update: { 
        title: 'Usuário atualizado!', 
        message: 'As alterações foram salvas com sucesso.' 
      },
    },
    errorMessages: {
      update: 'Erro ao atualizar usuário',
    },
  });

  /**
   * Loading State
   */
  if (isLoading) {
    return (
      <div className={styles.userDetailPage}>
        <div className={styles.loadingContainer}>
          <FiLoader size={48} className={styles.spinner} />
          <p className={styles.loadingText}>Carregando dados do usuário...</p>
        </div>
      </div>
    );
  }

  /**
   * Error State
   */
  if (isError || !user) {
    return (
      <div className={styles.userDetailPage}>
        <div className={styles.errorContainer}>
          <FiAlertCircle size={64} className={styles.errorIcon} />
          <h2 className={styles.errorTitle}>Erro ao carregar usuário</h2>
          <p className={styles.errorMessage}>
            {error instanceof Error ? error.message : 'Usuário não encontrado'}
          </p>
          <div style={{ display: 'flex', gap: 'var(--spacing-md)' }}>
            <Button variant="secondary" onClick={handleBack}>
              Voltar
            </Button>
            <Button variant="primary" onClick={() => refetch()}>
              Tentar Novamente
            </Button>
          </div>
        </div>
      </div>
    );
  }

  /**
   * Handle form submit
   */
  const handleFormSubmit = async (data: UserFormData) => {
    await updateMutation.mutateAsync(data);
  };

  return (
    <div className={styles.userDetailPage}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <button
            className={styles.backButton}
            onClick={handleBack}
            aria-label="Voltar"
          >
            <FiArrowLeft size={20} />
          </button>
          <div className={styles.headerTitle}>
            <h1 className={styles.title}>Detalhes do Usuário</h1>
            <p className={styles.subtitle}>Informações completas do usuário</p>
          </div>
        </div>
        <div className={styles.headerActions}>
          <Button
            variant="secondary"
            size="md"
            iconLeft={<FiRefreshCw size={16} />}
            onClick={() => refetch()}
          >
            Atualizar
          </Button>
          <Button
            variant="primary"
            size="md"
            iconLeft={<FiEdit2 size={16} />}
            onClick={() => setIsEditModalOpen(true)}
          >
            Editar
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className={styles.content}>
        {/* Profile Card */}
        <div className={`${styles.card} ${styles.profileCard}`}>
          <div className={styles.cardHeader}>
            <div className={styles.cardIcon}>
              <FiUser size={20} />
            </div>
            <h2 className={styles.cardTitle}>Perfil do Usuário</h2>
          </div>
          <div className={styles.profileContent}>
            <div className={styles.avatar}>
              {user.firstName.charAt(0).toUpperCase()}
              {user.lastName.charAt(0).toUpperCase()}
            </div>
            <div className={styles.profileInfo}>
              <h3 className={styles.userName}>
                {user.firstName} {user.lastName}
              </h3>
              <p className={styles.userEmail}>{user.email}</p>
              <div className={styles.userMeta}>
                <div className={styles.metaItem}>
                  <span className={`${styles.statusBadge} ${styles[user.status]}`}>
                    {getStatusIcon(user.status)}
                    {getStatusLabel(user.status)}
                  </span>
                </div>
                <div className={styles.metaItem}>
                  <FiHome size={16} className={styles.metaIcon} />
                  Organização ID: {user.organizationId}
                </div>
                <div className={styles.metaItem}>
                  <FiCalendar size={16} className={styles.metaIcon} />
                  Criado em {formatDate(user.createdAt)}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Account Information Card */}
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <div className={styles.cardIcon}>
              <FiMail size={20} />
            </div>
            <h2 className={styles.cardTitle}>Informações da Conta</h2>
          </div>
          <div className={styles.infoList}>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Email</span>
              <span className={styles.infoValue}>{user.email}</span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Status</span>
              <span className={`${styles.statusBadge} ${styles[user.status]}`}>
                {getStatusIcon(user.status)}
                {getStatusLabel(user.status)}
              </span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Organização</span>
              <span className={styles.infoValue}>ID: {user.organizationId}</span>
            </div>
          </div>
        </div>

        {/* System Information Card */}
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <div className={styles.cardIcon}>
              <FiHash size={20} />
            </div>
            <h2 className={styles.cardTitle}>Informações do Sistema</h2>
          </div>
          <div className={styles.infoList}>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>ID do Usuário</span>
              <span className={styles.infoValue}>{user.id}</span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Referência (UUID)</span>
              <span className={styles.infoValue}>{user.ref}</span>
            </div>
            {user.sub && (
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Subject ID</span>
                <span className={styles.infoValue}>{user.sub}</span>
              </div>
            )}
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Criado em</span>
              <span className={styles.infoValue}>{formatDate(user.createdAt)}</span>
            </div>
            {user.updatedAt && (
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Última atualização</span>
                <span className={styles.infoValue}>{formatDate(user.updatedAt)}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit User Modal */}
      <Modal
        open={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title="Editar Usuário"
        description="Atualize as informações do usuário"
        size="md"
      >
        <UserForm
          mode="edit"
          initialValues={user}
          onSubmit={handleFormSubmit}
          onCancel={() => setIsEditModalOpen(false)}
          isLoading={updateMutation.isPending}
        />
      </Modal>
    </div>
  );
};

export default UserDetailPage;
